import React, { useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { UiIcon, UiDialogContent, UiButton } from 'ui-components';
import { toggleStoryDetailModalState } from 'store';
import { randomNumber, platformColors, magicNumbers } from 'utils';
import { RelativeDateTime } from 'components';
import useArrows from 'components/story-swiper/useArrows';
import SendToBulletin from 'components/send-to-bulletin/SendToBulletin';
import is_verified_img from './../../assets/img/verified.png';
import { StyledInstagramStoryModal } from './InstagramStoryModal.styles';

const InstagramStoryModal = ({
  stories,
  sendToBulletin = false,
  renderDeleteButton,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const videoRef = useRef(null);
  const open = useSelector((state) => state.storyReducer.storyDetailModal);
  const selectedStoryIndex = useSelector(
    (state) => state.storyReducer.selectedStoryIndex
  );
  const hasBulletinAddPermission = useSelector(
    (state) =>
      state.accountReducer.currentUser?.user?.permissions?.Bulletin
        ?.addToBulletin?.permission
  );

  const storiesLength = stories?.length - 1 || 0;
  const story = stories[selectedStoryIndex];

  const storyCreatedAt = new Date(story?.dateTime);
  const currentDate = new Date();
  const timeDifference = currentDate - storyCreatedAt;

  // برای اینکه با هر کلیک روی استوری، رنگ اواتار تغییر نکنه
  const rndNumber = useMemo(() => randomNumber(9), [selectedStoryIndex]);

  const { handleNext, handlePrev } = useArrows({ stories });

  const handleClose = () => {
    dispatch(toggleStoryDetailModalState());
  };

  /**
   * باید حتما قبل از رفتن به صفحه پروفایل مودال بسته بشه
   * اگر از تگ لینک استفاده بشه و برگشت زده بشه مودال دوباره باز میشه که مشکلات زیادی داره
   **/
  const gotoProfile = async () => {
    await dispatch(toggleStoryDetailModalState());
    navigate(`/resources/instagram/${story?.platformUserId}`);
  };

  useEffect(() => {
    videoRef.current?.load();
    videoRef.current?.pause();
  }, [selectedStoryIndex]);

  const renderPrevButton = (className = '') => (
    <UiIcon
      className={`story-arrow ${className} ${
        selectedStoryIndex <= 0 ? 'disabled' : ''
      }`}
      icon='ri-arrow-right-s-line'
      size={20}
      onClick={handlePrev}
    />
  );

  const renderNextButton = (className = '') => (
    <UiIcon
      className={`story-arrow ${className} ${
        selectedStoryIndex >= storiesLength ? 'disabled' : ''
      }`}
      icon='ri-arrow-left-s-line'
      size={20}
      onClick={handleNext}
    />
  );

  if (!story) {
    return null;
  }

  const renderHeader = () => (
    <div className='story-info-header'>
      <div className='story-user'>
        {story?.avatar ? (
          <img
            className='story-user-avatar'
            src={story?.avatar}
            onClick={gotoProfile}
          />
        ) : (
          <span
            className={`story-user-avatar-text rand-bg-${rndNumber}`}
            onClick={gotoProfile}
          >
            {story?.charAvatar}
          </span>
        )}

        <span className='story-user-info'>
          <span className='story-user-name'>
            <span className='name' onClick={gotoProfile}>
              {story?.name}
            </span>
            {story?.isVerified && (
              <span className='p-8 flex vip'>
                <img src={is_verified_img} width='16' />
              </span>
            )}
          </span>
          <span className='story-user-username'>
            <span onClick={gotoProfile}>
              <UiIcon
                icon='ri-instagram-line'
                size={18}
                color={platformColors.instagram}
              />
            </span>
            <span className='username' onClick={gotoProfile}>
              {story?.username
                ? `@${story?.username}`
                : t('common:instagramNotDetermined')}
            </span>
          </span>
        </span>
      </div>
      <div className='story-datetime'>
        <RelativeDateTime dateTime={story?.dateTime} />
      </div>
    </div>
  );

  const renderStoryImage = () => (
    <div className='story-image-wrapper'>
      {renderPrevButton('story-image-arrow')}
      <div className='story-image'>
        {story?.mediaType === 'image' && <img src={story?.image?.[0]} />}
        {story?.mediaType === 'video' && (
          <video
            width='100%'
            controls
            preload='none'
            poster={story?.image?.[0]}
            autoPlay
            ref={videoRef}
          >
            <source src={story?.video?.[0]} />

            {t('common:yourBrowserDoesNotSupportTheVideoTag')}
          </video>
        )}
        <div className='story-image-datetime'>
          <RelativeDateTime dateTime={story?.dateTime} />
        </div>
      </div>
      {renderNextButton('story-image-arrow')}
    </div>
  );

  const renderBody = () => (
    <>
      <div className='story-info-tags flex between'>
        {story?.isVipResource ? (
          <span className='badge danger'>
            <UiIcon icon='ri-bookmark-2-line' color='#F7936F' />
            {t('content:vipResource')}
          </span>
        ) : (
          <span></span>
        )}
        {sendToBulletin && hasBulletinAddPermission && (
          <SendToBulletin
            ids={stories?.[selectedStoryIndex]?.id}
            platform='instagramStories'
          >
            {(setOpen) => (
              <UiButton
                className='action'
                variant='text'
                size='small'
                startIcon={
                  <UiIcon icon='ri-folder-upload-line' color='primary' />
                }
                onClick={() => {
                  setOpen(true);
                }}
              >
                {t('content:sendToBulletin')}
              </UiButton>
            )}
          </SendToBulletin>
        )}
      </div>
      {story?.topCategory && (
        <div className='story-info-category'>
          <span className='story-info-title'>
            {t('content:contentCategoryTitle')}
          </span>
          <span className='category'>{story?.topCategory}</span>
        </div>
      )}
      {story?.description && (
        <div className='story-info-content'>
          <div className='story-info-title'>{t('content:contentTitle')}</div>
          <div className='content'>{story?.description}</div>
        </div>
      )}
      {story?.mentions?.length > 0 && (
        <div className='story-info-extracted'>
          {story?.mentions?.map((mention, index) => (
            <bdi key={index} className='extracted'>
              @{mention}
            </bdi>
          ))}
        </div>
      )}
      {story?.hashtags?.length > 0 && (
        <div className='story-info-extracted'>
          {story?.hashtags?.map((hashtag, index) => (
            <bdi key={index} className='extracted'>
              {hashtag}
            </bdi>
          ))}
        </div>
      )}
      <div className='story-info-links'>
        {story?.id &&
          story?.username &&
          timeDifference < magicNumbers.ONE_DAY_IN_MILLISECONDS && (
            <div className='link'>
              <UiIcon icon='ri-link-m' size={20} onClick={handleClose} />
              <a
                href={`https://www.instagram.com/stories/${story?.username}/${story?.id}/`}
                target='_blank'
                rel='noreferrer'
              >
                {t('content:mainContentLink')}
              </a>
            </div>
          )}
        {story?.urls?.length > 0 &&
          story?.urls?.map((url, index) => (
            <div className='link' key={index}>
              <UiIcon icon='ri-link-m' size={20} onClick={handleClose} />
              <a href={url} target='_blank' rel='noreferrer'>
                {t('content:extractedLink')}
              </a>
            </div>
          ))}
      </div>
      {renderDeleteButton && renderDeleteButton(stories[selectedStoryIndex])}
    </>
  );

  return (
    <StyledInstagramStoryModal
      open={open}
      onClose={handleClose}
      className='instagram-story-modal'
    >
      <UiDialogContent>
        <div className='story'>
          <UiIcon
            className='story-close'
            icon='ri-close-line'
            size={20}
            onClick={handleClose}
          />
          {renderPrevButton()}
          <div className='story-info'>
            {renderHeader()}
            {renderBody()}
          </div>
          {renderStoryImage()}
          {renderNextButton()}
        </div>
      </UiDialogContent>
    </StyledInstagramStoryModal>
  );
};

InstagramStoryModal.propTypes = {
  stories: PropTypes.array,
  sendToBulletin: PropTypes.bool,
  renderDeleteButton: PropTypes.func,
};

export default InstagramStoryModal;
